import React, { Fragment, useState } from 'react';
import { Box, Button, TextField, IconButton, Typography, Chip, Stack, SelectChangeEvent, Divider } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { DeleteRounded } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import {
  addElementIdToRecipient,
  removeElementIdFromRecipients,
  selectRecipients,
  selectedElement,
  updateContentElementById,
} from 'features/document/documentSlice';
import { CheckboxItem } from 'features/document/constants';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { isCheckboxElement } from 'utils/typeGuards';
import { colorPalette, docGeniusPalette } from 'styles/partials/colors';
import RecipientSelect from 'components/RecipientSelect/RecipientSelect';
import { Input } from 'pages/DocumentBuilder/components/ContentSection/components/ElementSection/components/CheckBox/Checkbox.styled';

interface CheckboxSidebarProps {
  currentElement: selectedElement;
}

export default function CheckboxSidebar({ currentElement }: CheckboxSidebarProps) {
  if (!isCheckboxElement(currentElement.data)) {
    return null;
  }

  const dispatch = useAppDispatch();

  const {
    data: { recipient: currentRecipient },
  } = currentElement;

  const recipients = useAppSelector(selectRecipients);

  const checkboxElement = currentElement.data;
  if (!isCheckboxElement(checkboxElement)) {
    return null;
  }

  const [checkboxValues, setCheckboxValues] = useState<CheckboxItem[]>(checkboxElement.content.elements);
  const groupTooltip = checkboxElement.content.groupTooltip;
  const groupDirection = checkboxElement.content.groupDirection;
  const isDeleteButtonDisabled = checkboxValues.length === 1;

  const updateCheckboxState = (updatedCheckboxes: CheckboxItem[]) => {
    dispatch(
      updateContentElementById({
        content: {
          elements: updatedCheckboxes,
        },
        style: {},
      })
    );
    setCheckboxValues(updatedCheckboxes);
  };

  const handleOnChangeRecipient = (event: SelectChangeEvent) => {
    dispatch(removeElementIdFromRecipients(checkboxElement.id));
    const selectedRecipient = recipients.find(recipient => recipient.fullName === event.target.value);
    if (selectedRecipient) {
      dispatch(addElementIdToRecipient({ recipientId: selectedRecipient.id, elementId: checkboxElement.id }));

      dispatch(
        updateContentElementById({
          content: {},
          style: {},
          recipient: selectedRecipient,
        })
      );
    }
  };

  const handleAddCheckbox = () => {
    const newCheckboxId = uuidv4();
    const newCheckbox = { id: newCheckboxId, label: `Label`, checked: false };

    updateCheckboxState([...checkboxValues, newCheckbox]);
  };

  const handleRemoveCheckbox = (id: string) => {
    const updatedCheckboxes = checkboxValues.filter(checkbox => checkbox.id !== id);
    updateCheckboxState(updatedCheckboxes);
  };

  const handleLabelChange = (id: string, newLabel: string) => {
    const updatedCheckboxes = checkboxValues.map(checkbox =>
      checkbox.id === id ? { ...checkbox, label: newLabel } : checkbox
    );
    updateCheckboxState(updatedCheckboxes);
  };

  const handleGroupTooltipChange = (newGroupTooltip: string) => {
    dispatch(
      updateContentElementById({
        content: {
          groupTooltip: newGroupTooltip,
        },
        style: {},
      })
    );
  };

  const handleCheckboxToggle = (id: string) => {
    const updatedCheckboxes = checkboxValues.map(checkbox =>
      checkbox.id === id ? { ...checkbox, checked: !checkbox.checked } : checkbox
    );
    updateCheckboxState(updatedCheckboxes);
  };

  const handleOnClickGroupDirection = (direction: string) => {
    dispatch(
      updateContentElementById({
        content: {
          groupDirection: direction,
        },
        style: {},
      })
    );
  };

  return (
    <Box alignItems={'flex-start'} display={'flex'} flexDirection={'column'}>
      <Typography color={colorPalette.darkGray1} fontSize={'16px'} fontWeight={500} marginBottom={'32px'}>
        Checkbox Block
      </Typography>
      <Box display={'flex'} flexDirection={'column'} gap={'24px'} width='100%'>
        <Typography color={colorPalette.darkGray1} fontSize={'14px'} fontWeight={500}>
          Recipient
        </Typography>
        <RecipientSelect
          value={currentRecipient}
          recipientOptions={recipients}
          editRecipients={false}
          onChange={handleOnChangeRecipient}
        />
      </Box>
      <Divider
        color='#C9CACB'
        flexItem
        orientation='horizontal'
        variant='middle'
        sx={{ margin: '24px 0', width: '100%' }}
      />
      {checkboxElement.content.elements.length > 1 && (
        <Fragment>
          <Typography color={colorPalette.darkGray1} fontSize={'14px'} fontWeight={500} marginBottom={'24px'}>
            Group Tooltip
          </Typography>
          <TextField
            id={`group-tooltip`}
            value={groupTooltip}
            fullWidth
            placeholder='Add text'
            onChange={e => handleGroupTooltipChange(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                height: '48px',
              },
            }}
          />
          <Typography
            color={colorPalette.darkGray1}
            fontSize={'14px'}
            fontWeight={500}
            marginTop={'32px'}
            marginBottom={'24px'}
          >
            Group Direction
          </Typography>
          <Stack direction='row' spacing={1} marginBottom={'32px'}>
            <Chip
              variant={groupDirection === 'vertical' ? 'filled' : 'outlined'}
              label='Vertical'
              onClick={() => handleOnClickGroupDirection('vertical')}
            />
            <Chip
              variant={groupDirection === 'horizontal' ? 'filled' : 'outlined'}
              label='Horizontal'
              onClick={() => handleOnClickGroupDirection('horizontal')}
            />
          </Stack>
        </Fragment>
      )}
      <Typography color={colorPalette.darkGray1} fontSize={'14px'} fontWeight={500} marginBottom={'24px'}>
        Checkbox Values
      </Typography>
      {checkboxValues.map(({ id, label, checked }) => (
        <Box
          alignItems='center'
          color={colorPalette.darkGray1}
          display='flex'
          fontSize='12px'
          fontWeight={400}
          gap='16px'
          key={id}
          marginBottom='16px'
        >
          <Input checked={checked} type='checkbox' onChange={() => handleCheckboxToggle(id)} />
          <TextField
            id={`checkbox-label-${id}`}
            value={label}
            fullWidth
            placeholder='Add text'
            onChange={e => handleLabelChange(id, e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                height: '48px',
              },
            }}
          />
          <IconButton
            aria-label={`Remove Checkbox ${id}`}
            disabled={isDeleteButtonDisabled}
            color='primary'
            sx={{ height: '40px', width: '40px' }}
            onClick={() => handleRemoveCheckbox(id)}
          >
            <DeleteRounded />
          </IconButton>
        </Box>
      ))}
      <Button
        variant='outlined'
        startIcon={<AddRoundedIcon />}
        sx={{ marginTop: '16px', textTransform: 'none', borderColor: docGeniusPalette.primaryBlue }}
        onClick={handleAddCheckbox}
      >
        Add Checkbox
      </Button>
    </Box>
  );
}
